import React, {useEffect, useState} from 'react';

import cs from 'classnames';
import {FineUploaderBasic} from 'fine-uploader/lib/core';
import FileInput from 'react-fine-uploader/file-input';
import {useDispatch} from 'react-redux';

import {useAccountSettingsState} from 'app/settings/api';
import {showNotification} from 'app/toasts/slice';
import {preparedUploader} from 'app/utils/fineuploader';
import {gt} from 'app/utils/i18n';

import type {BaseAttachedImage, S3UploadConfigType} from 'sweet-types';

interface Props {
  onAddImage: (file: BaseAttachedImage) => void;
  setSubmittedImages: React.Dispatch<React.SetStateAction<number[]>>;
  uploaderRef: React.MutableRefObject<FineUploaderBasic>;
  inputLabel?: string;
  iconSizeClass?: string;
}

export function ImageAttachmentsUploader({
  onAddImage,
  setSubmittedImages,
  uploaderRef,
  inputLabel = gt('Attach'),
  iconSizeClass = '',
}: Props) {
  const settings = useAccountSettingsState().data!;
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploaderReady, setIsUploaderReady] = useState(false);

  useEffect(() => {
    if (!uploaderRef.current) {
      uploaderRef.current = preparedUploader(
        settings.s3Upload.IMAGES as S3UploadConfigType,
        settings.account_id,
        settings.MEDIA_URL
      );

      uploaderRef.current.on('submitted', (id: number) => {
        setSubmittedImages((prev) => [...prev, id]);
        setIsLoading(true);
      });

      uploaderRef.current.on(
        'complete',
        (id: number, name: string, response: any) => {
          if (response.success) {
            onAddImage({
              image_ref: uploaderRef.current.methods.getKey(id),
            });
          }
        }
      );

      uploaderRef.current.on('error', (error) => {
        showNotification(error.toString(), 'error');
        setIsLoading(false);
        throw error;
      });
    }
    setIsUploaderReady(true);

    return () => {
      if (uploaderRef.current) {
        uploaderRef.current.off('submitted');
        uploaderRef.current.off('complete');
        uploaderRef.current.off('error');
      }
    };
  }, [settings, onAddImage, dispatch, setSubmittedImages, uploaderRef]);

  if (!isUploaderReady) {
    return null;
  }

  const acceptTypes = 'image/png,image/jpeg,image/gif';

  return (
    <div className="file-upload-container">
      <FileInput
        uploader={uploaderRef.current}
        accept={acceptTypes}
        className={cs('btn btn-link btn-sm', {'text-dark': isLoading})}
        disabled={!settings.can_upload_files}
      >
        <i
          className={cs('fa mr-1', iconSizeClass, {
            'fa-image': !isLoading,
            'fa-circle-notch fa-spin': isLoading,
          })}
        />
        {inputLabel}
      </FileInput>
    </div>
  );
}
